// Effects for one product side (solo scan, or one side of a compare/compat pair): runs the scan v2 calls,
// feeds the answers to the pure sideReducer and refreshes the credit balance
// after every charged step.
import { useCallback, useEffect, useMemo, useReducer, useRef, useState } from 'react';
import * as scanApi from '../../../api/scanV2';
import { isApiError, isCreditError } from '../../../api/config';
import { useUser } from '../../../contexts/UserContext';
import { downscaleForIdentify, downscaleLabelParts } from '../../../utils/downscaleImage';
import { getErrorMessage } from '../../../utils/errors';
import type { LabelMeta, ScanAnswer, ScanContext } from '../../../types/scanV2';
import {
  initialSideState,
  knownReasonMessage,
  reasonMessage,
  sideReducer,
  type BrandNamePending,
  type SideAction,
  type SideProduct,
  type SideState,
} from './reducer';

export type SideFlowContext = ScanContext;

export const NOT_ENOUGH_CREDITS_MESSAGE = 'Not enough credits. Top up and try again.';

export interface ProductSideFlow {
  state: SideState;
  identifyBarcode: (code: string) => Promise<void>;
  identifyPhoto: (file: Blob) => Promise<void>;
  confirm: (candidateId: string) => Promise<void>;
  rejectAll: () => Promise<void>;
  notMine: () => Promise<void>;
  addPart: (file: Blob) => void;
  removePart: (index: number) => void;
  readLabel: (meta?: LabelMeta) => Promise<void>;
  pasteText: (text: string, meta?: LabelMeta) => Promise<void>;
  sendToTeam: (meta: LabelMeta) => Promise<void>;
  /** Answer the brand and name prompt: re-runs the step that asked for it. */
  submitBrandName: (meta: LabelMeta) => Promise<void>;
  pickFavorite: (product: SideProduct) => void;
  /** Brand and name given once in this label session (or read from the photo); sent with every later step. */
  rememberedMeta?: LabelMeta;
  /** Change the remembered brand and name before the next step sends them. */
  editMeta: (meta: LabelMeta) => void;
  /** Leave the brand and name prompt. */
  cancel: () => void;
  /** Leave the error card: run a failed identify again, else go back to the step it came from. */
  retry: () => void;
  clear: () => void;
  /** The last answer refunded the session's credit (not mine, nothing left to pick). */
  creditReturned: boolean;
  /** Photograph the label to confirm a ready product's unverified (AI) list: same scan, free. */
  verifyWithLabel: () => void;
  /** Leave that label step and keep the unverified list. */
  cancelVerify: () => void;
}

/** The answer moved credits (a charge or a refund), so the balance is stale. */
function movedCredits(answer: ScanAnswer): boolean {
  return (
    ('charged' in answer && answer.charged === true) ||
    ('refunded' in answer && answer.refunded) ||
    answer.usage_after !== undefined
  );
}

function isBrandNameRequired(error: unknown): boolean {
  if (!isApiError(error) || error.status !== 400) return false;
  const payload = error.payload as { reason?: unknown } | null;
  return payload?.reason === 'brand_name_required';
}

function failureFor(error: unknown): SideAction {
  if (isCreditError(error)) {
    return error.status === 402
      ? { type: 'FAIL', message: NOT_ENOUGH_CREDITS_MESSAGE, status: 402 }
      : { type: 'FAIL', message: error.message, status: error.status };
  }
  if (isApiError(error)) {
    // Scan bodies on other statuses (e.g. 503 provider_unavailable) carry a reason, not an error text.
    const reason = (error.payload as { reason?: unknown } | null)?.reason;
    return { type: 'FAIL', message: knownReasonMessage(reason) ?? error.message, status: error.status };
  }
  return { type: 'FAIL', message: getErrorMessage(error, reasonMessage(null)) };
}

/** Brand and name are only worth reusing when both are filled in. */
function hasBrandName(meta?: LabelMeta): meta is LabelMeta {
  if (!meta) return false;
  return Boolean(meta.brand?.trim() && meta.name?.trim());
}

/** Brand, name and variant with blanks dropped, or undefined when nothing is left. */
function cleanMeta(meta?: LabelMeta): LabelMeta | undefined {
  const brand = meta?.brand?.trim();
  const name = meta?.name?.trim();
  const variant = meta?.variant?.trim();
  if (!brand && !name) return undefined;
  return { ...(brand ? { brand } : {}), ...(name ? { name } : {}), ...(variant ? { variant } : {}) };
}

function labelScanId(state: SideState): string | null {
  if (state.step === 'label' || state.step === 'fallback' || state.step === 'needsBrandName') return state.scanId;
  return null;
}

export function useProductSideFlow(context: SideFlowContext, initial?: SideProduct): ProductSideFlow {
  const [state, dispatch] = useReducer(sideReducer, initial, initialSideState);
  const { refreshCredits } = useUser();
  const stateRef = useRef(state);
  stateRef.current = state;
  const requestRef = useRef(0);
  /** A billable call is on the wire: a second tap must not start another one. */
  const inFlightRef = useRef(false);
  const mountedRef = useRef(true);
  const [rememberedMeta, setRememberedMeta] = useState<LabelMeta>();
  const [creditReturned, setCreditReturned] = useState(false);
  const metaRef = useRef<LabelMeta>();
  /** The last identify call, so "Try again" can send it again. */
  const lastIdentifyRef = useRef<(() => Promise<void>) | null>(null);
  /** Keep the brand and name the user typed; otherwise fall back to the remembered ones. */
  const withMeta = useCallback((meta?: LabelMeta): LabelMeta | undefined => {
    if (hasBrandName(meta)) {
      metaRef.current = meta;
      setRememberedMeta(meta);
      return meta;
    }
    return metaRef.current ?? meta;
  }, []);
  const forgetMeta = useCallback(() => {
    metaRef.current = undefined;
    setRememberedMeta(undefined);
  }, []);
  /** Remember a brand and name (from a photo hint or the user's edit) for the next label step. */
  const editMeta = useCallback((meta: LabelMeta) => {
    const cleaned = cleanMeta(meta);
    metaRef.current = cleaned;
    setRememberedMeta(cleaned);
  }, []);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      requestRef.current += 1;
    };
  }, []);

  /**
   * Dispatch `start`, run `call`, then dispatch its answer. Answers that land
   * after clear() or unmount (a newer request id) are dropped.
   */
  const run = useCallback(
    async <T extends ScanAnswer>(
      start: SideAction,
      call: () => Promise<T>,
      done: (answer: T) => SideAction,
      brandName?: { pending: BrandNamePending; text?: string },
    ): Promise<void> => {
      if (inFlightRef.current) return;
      inFlightRef.current = true;
      requestRef.current += 1;
      const id = requestRef.current;
      const live = () => mountedRef.current && id === requestRef.current;
      setCreditReturned(false);
      dispatch(start);
      try {
        const answer = await call();
        // Refresh even when the answer is dropped (clear/unmount): the server already moved credits.
        if (movedCredits(answer)) void refreshCredits();
        if (!live()) return;
        if ('refunded' in answer && answer.refunded) setCreditReturned(true);
        dispatch(done(answer));
      } catch (error) {
        if (!live()) return;
        if (brandName && isBrandNameRequired(error)) {
          dispatch({ type: 'NEED_BRAND_NAME', ...brandName });
          return;
        }
        dispatch(failureFor(error));
      } finally {
        if (id === requestRef.current) inFlightRef.current = false;
      }
    },
    [refreshCredits],
  );

  const identifyBarcode = useCallback(
    async (code: string) => {
      const barcode = code.trim();
      if (!barcode) return;
      forgetMeta();
      lastIdentifyRef.current = () => identifyBarcode(barcode);
      await run(
        { type: 'IDENTIFY_START', barcode },
        () => scanApi.identifyBarcode(barcode, context),
        (result) => ({ type: 'IDENTIFIED', result }),
      );
    },
    [context, forgetMeta, run],
  );

  const identifyPhoto = useCallback(
    (file: Blob): Promise<void> => {
      forgetMeta();
      lastIdentifyRef.current = () => identifyPhoto(file);
      return run(
        { type: 'IDENTIFY_START' },
        async () => scanApi.identifyImage(await downscaleForIdentify(file), context),
        (result) => {
          // Nothing found: keep what the photo read, so the label step does not ask again.
          if (result.hint) editMeta(result.hint);
          return { type: 'IDENTIFIED', result };
        },
      );
    },
    [context, editMeta, forgetMeta, run],
  );

  const confirm = useCallback(
    async (candidateId: string) => {
      const s = stateRef.current;
      if (s.step !== 'candidates') return;
      await run(
        { type: 'CONFIRM_START', candidateId },
        () => scanApi.confirmCandidate(s.scanId, candidateId),
        (result) => ({ type: 'CONFIRMED', result }),
      );
    },
    [run],
  );

  const rejectAll = useCallback(async () => {
    const s = stateRef.current;
    if (s.step !== 'candidates') return;
    const ids = s.candidates.map((c) => c.candidate_id);
    await run({ type: 'REJECT_START' }, () => scanApi.rejectCandidates(s.scanId, ids), (result) => ({ type: 'REJECTED', result }));
  }, [run]);

  const notMine = useCallback(async () => {
    const s = stateRef.current;
    if (s.step !== 'ready' || !s.canNotMine || !s.product.scanId) return;
    const scanId = s.product.scanId;
    await run({ type: 'NOT_MINE_START' }, () => scanApi.notMine(scanId), (result) => ({ type: 'REJECTED', result }));
  }, [run]);

  const readLabel = useCallback(
    async (meta?: LabelMeta) => {
      const s = stateRef.current;
      const label = s.step === 'needsBrandName' ? s.back : s;
      if (label.step !== 'label' || label.parts.length === 0) return;
      const { scanId, parts } = label;
      const sent = withMeta(meta);
      await run(
        { type: 'LABEL_START' },
        async () => scanApi.uploadLabel(scanId, await downscaleLabelParts(parts), sent),
        (result) => ({ type: 'LABEL_RESULT', result, meta: sent }),
        { pending: 'label' },
      );
    },
    [run, withMeta],
  );

  const pasteText = useCallback(
    async (text: string, meta?: LabelMeta) => {
      const scanId = labelScanId(stateRef.current);
      if (!scanId || !text.trim()) return;
      const sent = withMeta(meta);
      await run(
        { type: 'LABEL_START' },
        () => scanApi.pasteLabelText(scanId, { text, ...sent }),
        (result) => ({ type: 'LABEL_RESULT', result, meta: sent }),
        { pending: 'paste', text },
      );
    },
    [run, withMeta],
  );

  const sendToTeam = useCallback(
    async (meta: LabelMeta) => {
      const scanId = labelScanId(stateRef.current);
      if (!scanId) return;
      const sent = withMeta(meta) ?? meta;
      await run(
        { type: 'LABEL_START' },
        () => scanApi.submitToTeam(scanId, sent),
        (result) => ({ type: 'LABEL_RESULT', result, meta: sent }),
        { pending: 'team' },
      );
    },
    [run, withMeta],
  );

  const submitBrandName = useCallback(
    async (meta: LabelMeta) => {
      const s = stateRef.current;
      if (s.step !== 'needsBrandName') return;
      if (s.pending === 'label') await readLabel(meta);
      else if (s.pending === 'paste') await pasteText(s.text ?? '', meta);
      else await sendToTeam(meta);
    },
    [pasteText, readLabel, sendToTeam],
  );

  const addPart = useCallback((part: Blob) => { dispatch({ type: 'ADD_PART', part }); }, []);
  const removePart = useCallback((index: number) => { dispatch({ type: 'REMOVE_PART', index }); }, []);
  const pickFavorite = useCallback((product: SideProduct) => {
    forgetMeta();
    requestRef.current += 1;
    inFlightRef.current = false;
    dispatch({ type: 'PICK_FAVORITE', product });
  }, [forgetMeta]);
  const cancel = useCallback(() => { dispatch({ type: 'CANCEL' }); }, []);
  const retry = useCallback(() => {
    const s = stateRef.current;
    const again = lastIdentifyRef.current;
    if (s.step === 'error' && s.retryIdentify && again) {
      void again();
      return;
    }
    dispatch({ type: 'RETRY' });
  }, []);
  const clear = useCallback(() => {
    forgetMeta();
    requestRef.current += 1;
    inFlightRef.current = false;
    setCreditReturned(false);
    dispatch({ type: 'CLEAR' });
  }, [forgetMeta]);

  const verifyWithLabel = useCallback(() => { dispatch({ type: 'VERIFY_START' }); }, []);
  const cancelVerify = useCallback(() => { dispatch({ type: 'VERIFY_CANCEL' }); }, []);

  return useMemo(
    () => ({
      state,
      identifyBarcode,
      identifyPhoto,
      confirm,
      rejectAll,
      notMine,
      addPart,
      removePart,
      readLabel,
      pasteText,
      sendToTeam,
      submitBrandName,
      pickFavorite,
      rememberedMeta,
      editMeta,
      cancel,
      retry,
      clear,
      creditReturned,
      verifyWithLabel,
      cancelVerify,
    }),
    [state, identifyBarcode, identifyPhoto, confirm, rejectAll, notMine, addPart, removePart, readLabel, pasteText, sendToTeam, submitBrandName, pickFavorite, rememberedMeta, editMeta, cancel, retry, clear, creditReturned, verifyWithLabel, cancelVerify],
  );
}
